<template>
    <div v-if="loaded">
        <rkm-alert
            v-if="form.materiaisComMovimentacoesSemLivro.length > 0"
            color="primary"
        >
            <span>Existem movimentações de medicamentos sem livro disponível para registrar.</span>
            <rkm-button
                @click="$refs.ModalMaterialSemLivro.open()"
                class="ml-2"
                color="primary"
                small
            >
                Visualizar medicamentos
            </rkm-button>
        </rkm-alert>

        <rkm-row>
            <rkm-col cols="4">
                <rkm-form-select
                    :disabled="form.livrosRegistro.length === 1"
                    :options="form.livrosRegistro"
                    default-option-text="TODOS"
                    label="Livro"
                    show-default-option
                    text-field="nome"
                    v-model="model.livroRegistroId"
                    value-field="id"
                />
            </rkm-col>

            <rkm-col cols="4">
                <rkm-form-select
                    :options="form.tiposLivroRegistro"
                    default-option-text="TODOS"
                    label="Tipo"
                    show-default-option
                    text-field="nome"
                    v-model="model.livroRegistroTipoId"
                    value-field="id"
                />
            </rkm-col>

            <rkm-col cols="4">
                <rkm-form-select
                    :options="form.status"
                    default-option-text="TODOS"
                    label="Status"
                    show-default-option
                    text-field="nome"
                    v-model="model.statusId"
                    value-field="id"
                />
            </rkm-col>
        </rkm-row>

        <rkm-data-table
            :fields="fields"
            :filter="localFilter"
            :repository="repository"
            :request-params="requestParams"
            :tbody-tr-class="rowClass"
            :reset-table="resetTable"
            :search-by="localSearchOption"
            :search-table="searchTable"
            class="table-livros"
            ref="DataTable"
            sort-by="abertoEm"
            sort-desc
        >

            <template v-slot:filters>
                <rkm-form-input
                    placeholder="Digite para buscar"
                    v-model="localFieldFilter"
                    @keyup.native.enter="handleEnterKey"
                >
                    <rkm-input-group-prepend slot="prepend">
                        <rkm-form-select
                            :options="searchOptions"
                            class="m-0"
                            v-model="localSearchOption"
                        />
                    </rkm-input-group-prepend>

                    <rkm-input-group-append slot="append">

                        <rkm-button
                            :disabled="localFieldFilter || !disabledButtonSearch ? false : true"
                            @click="search"
                            color="primary"
                            class="btn-search"
                        >
                            Buscar
                        </rkm-button>
                        <rkm-button
                            :disabled="!localFieldFilter"
                            @click="clear"
                            class="btn-clear"
                        >
                            Limpar
                        </rkm-button>
                    </rkm-input-group-append>
                </rkm-form-input>
            </template>

            <template v-slot:cell(actions)="{ item }">
                <rkm-button
                    :authorize="`update-farmacia-${funcionalidade}`"
                    @click.stop="$router.push({ name: `farmacia-${funcionalidade}-edit`, params: { id: item.id } })"
                    color="primary"
                    small
                    title="Visualizar"
                >
                    <rkm-icon icon="eye" />
                </rkm-button>

                <rkm-button
                    :authorize="`update-farmacia-${funcionalidade}`"
                    :disabled="item.statusId === enums.livroStatus.FECHADO || !item.hasAvailableUpdate"
                    @click.stop="update(item.id)"
                    color="primary"
                    small
                    title="Atualizar movimentações"
                >
                    <rkm-icon icon="sync-alt" />
                </rkm-button>
            </template>

            <template #afterTable>
                <div class="my-2">
                    <p class="mb-0">
                        A linha em <span class="yellow">amarela</span> representa que o livro está com o
                        status aberto, porém já foi preenchido completamente.
                    </p>

                    <p>
                        A linha em <span class="blue">azul</span> representa que há movimentações disponíveis
                        para registrar no livro.
                    </p>
                </div>
            </template>
        </rkm-data-table>

        <ModalMaterialSemLivro
            :materiais="form.materiaisComMovimentacoesSemLivro"
            ref="ModalMaterialSemLivro"
        />
    </div>
</template>

<script>
// Components.
import ModalMaterialSemLivro from './components/material-sem-livro/Modal'

// Others.
import { mapActions } from 'vuex'
import { repository, error, formats } from '@/mixins'
import livroStatus from '@/enums/farmacia/livroStatus'

export default {
    components: { ModalMaterialSemLivro },
    created() {

        this.repositories = {
            livroPsicotropico: this.getConectaSusRepository('farmacia.livroPsicotropico'),
            livroTalidomida: this.getConectaSusRepository('farmacia.livroTalidomida'),
        }

        this.load()
    },
    computed: {
        isLivroPsicotropico() {

            return this.funcionalidade === 'livro-psicotropico'
        },
        isLivroTalidomida() {

            return this.funcionalidade === 'livro-talidomida'
        },
        repository() {

            if (this.isLivroPsicotropico) {

                return this.repositories.livroPsicotropico
            }
            else if (this.isLivroTalidomida) {

                return this.repositories.livroTalidomida
            }

            return null
        },
        requestParams() {

            return {
                livro_registro_id: this.model.livroRegistroId,
                livro_registro_tipo_id: this.model.livroRegistroTipoId,
                status_id: this.model.statusId,
            }
        },
        fields() {

            return [
                {
                    name: 'livro.numero',
                    key: 'numero',
                    label: 'Número',
                    searchable: true,
                    sortable: true,
                    class: 'col-min text-center',
                },
                {
                    name: 'livro.abertoEm',
                    key: 'abertoEm',
                    label: 'Data de abertura',
                    searchable: true,
                    sortable: true,
                    class: 'col-min text-center',
                    formatter: data => this.formatDate(data)
                },
                {
                    name: 'livroRegistro.nome',
                    key: 'livro',
                    label: 'Livro',
                    searchable: false,
                    sortable: true,
                },
                {
                    name: 'livroRegistroTipo.nome',
                    key: 'tipo',
                    label: 'Tipo',
                    searchable: false,
                    sortable: true,
                },
                {
                    name: 'status.nome',
                    key: 'status',
                    label: 'Status',
                    searchable: false,
                    sortable: true,
                    class: 'col-min text-center',
                },
                {
                    key: 'actions',
                    label: 'Ações',
                    class: 'col-min',
                },
            ]
        },
        searchOptions() {
            return this.fields.filter(field => !!field.searchable).map(field => {
                return { text: field.label, value: field.key }
            })
        },
    },
    data: () => ({
        loaded: false,
        disabledButtonSearch: true,
        form: null,
        model: null,
        enums: {
            livroStatus,
        },
        localFieldFilter: null,
        localFilter: null,
        localSearchOption: null,
        searchTable: false,
    }),
    methods: {
        ...mapActions(['setLoading', 'unsetLoading']),
        load(afterUpdate = false) {

            this.setLoading()

            this.repository.list()
                .then(({ data }) => {

                    if (afterUpdate) {

                        this.form.materiaisComMovimentacoesSemLivro = data.form.materiaisComMovimentacoesSemLivro

                        return
                    }

                    this.form = data.form
                    this.model = data.model

                    if (this.form.livrosRegistro.length === 1) {

                        this.model.livroRegistroId = this.form.livrosRegistro[0].id
                    }

                    this.loaded = true
                })
                .catch(this.handleError)
                .finally(() => this.unsetLoading())
        },
        update(id) {

            this.showConfirmation("Deseja realmente atualizar as movimentações?",
                async () => {

                    this.setLoading()

                    this.repository.update(id)
                        .then(() => {

                            this.showSaveNotification()
                            this.load(true)
                            this.$refs.DataTable.refresh()
                        })
                        .catch(this.handleError)
                        .finally(() => this.unsetLoading())
                }
            )
        },
        rowClass(item, type) {

            if (type === 'row' && item.statusId == this.enums.livroStatus.ABERTO) {

                if (item.totalmentePreenchido) {

                    return 'table-warning'
                }
                else if (item.hasAvailableUpdate) {

                    return 'table-primary'
                }
            }
        },
        search() {

            this.searchTable = true
            this.resetTable = false
            this.localFilter = this.localFieldFilter
            this.$refs.DataTable.refresh();
        },
        clear() {

            if (this.model) {
                this.model.livroRegistroId = null
                this.model.livroRegistroTipoId = null
                this.model.statusId = null
            }

            this.localSearchOption = this.searchBy || this.searchOptions[0].value;
            this.clearSearchOption()
        },
        clearSearchOption() {
            this.localFieldFilter = null
            this.showNaoRegularizados = false
            this.clearTable()
        },
        clearTable() {
            this.localFilter = null
            this.resetTable = true
            this.searchTable = false
        },
        handleEnterKey(event) {

            if (event.key === 'Enter' && (this.localFieldFilter || !this.disabledButtonSearch)) {
                this.search();
            }
        },
    },
    mounted() {
        if (this.searchOptions.length > 0) {
            this.localSearchOption = this.searchOptions[0].value;
        }
    },
    mixins: [repository, error, formats],
    props: {
        funcionalidade: { type: String, required: true },
    },
    watch: {
        'localSearchOption': function () {

            this.clearSearchOption()
        },
        'localFieldFilter': function () {

            this.clearTable()
        },
        'model': {
            deep: true,
            handler() {

                this.clearTable()
            },
        },
    },
}
</script>

<style scoped>
.table-livros >>> .yellow { color: #e6c619; }
.table-livros >>> .blue { color: blue; }
.btn-search {
    border-top-right-radius: 0.25rem!important;
    border-bottom-right-radius: 0.25rem!important;
}
.btn-clear {
    border-top-left-radius: 0.25rem!important;
    border-bottom-left-radius: 0.25rem!important;
}
</style>